import React, { useState, useEffect } from "react";
import { auth, db } from "../firebaseConfig";
import { collection, addDoc, getDocs, doc, deleteDoc, updateDoc } from "firebase/firestore";
import {
  Container,
  TextField,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
  Box,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

const RegistroRoles = () => {
  const [roles, setRoles] = useState([]);
  const [formVisible, setFormVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRoleId, setCurrentRoleId] = useState(null);
  const [formData, setFormData] = useState({ nombre: "" });
  const [alertMessage, setAlertMessage] = useState("");
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false); // Estado para abrir/ cerrar el modal de eliminación

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const rolesSnapshot = await getDocs(collection(db, "rol"));
        setRoles(rolesSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      } catch (error) {
        console.error("Error al obtener roles:", error);
      }
    };
    fetchRoles();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDelete = async (rolId) => {
    try {
      const rolRef = doc(db, "rol", rolId);
      await deleteDoc(rolRef);
      setRoles(roles.filter((rol) => rol.id !== rolId)); // Elimina el rol de la lista local
      setAlertMessage("Rol eliminado exitosamente.");
      setAlertSeverity("success");
      setAlertOpen(true); // Mostrar la alerta
    } catch (error) {
      console.error("Error al eliminar rol:", error);
      setAlertMessage("Ocurrió un error al eliminar el rol.");
      setAlertSeverity("error");
      setAlertOpen(true); // Mostrar la alerta
    }
    setOpenDeleteDialog(false); // Cerrar el modal
  };


  const handleOpenDeleteDialog = (rolId) => {
    setCurrentRoleId(rolId);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };
  const handleEdit = (rol) => {
    setFormVisible(true);  // Mostrar formulario
    setEditMode(true);  // Activar modo edición
    setCurrentRoleId(rol.id);  // Guardar el ID del rol actual
    setFormData({
      nombre: rol.nombre || "",  // Asegurar que siempre haya un valor
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const user = auth.currentUser;
      if (!user) {
        setAlertMessage("No tienes permisos para realizar esta acción.");
        setAlertSeverity("error");
        setAlertOpen(true);
        return;
      }

      const userId = user.uid;

      if (editMode) {
        const roleRef = doc(db, "rol", currentRoleId);
        await updateDoc(roleRef, { ...formData, userId });
        setRoles(roles.map((role) => (role.id === currentRoleId ? { id: currentRoleId, ...formData, userId } : role)));
        setAlertMessage("Rol actualizado exitosamente.");
      } else {
        const newRoleRef = await addDoc(collection(db, "rol"), { ...formData, userId });
        setRoles([...roles, { id: newRoleRef.id, ...formData, userId }]);
        setAlertMessage("Nuevo rol agregado exitosamente.");
      }

      setFormData({ nombre: "" });
      setFormVisible(false);
      setEditMode(false);
      setCurrentRoleId(null);
      setAlertSeverity("success");
      setAlertOpen(true);
    } catch (error) {
      console.error("Error al agregar o actualizar rol:", error);
      setAlertMessage("Ocurrió un error. Intenta nuevamente.");
      setAlertSeverity("error");
      setAlertOpen(true);
    }
  };

  return (
    <Container maxWidth="md" sx={{ paddingBottom: 2 }}>
      {!formVisible ? (
        <>
          <Typography variant="h5" sx={{ fontWeight: "bold", marginBottom: 2 }}>
            Lista de Roles
          </Typography>
          <Button variant="contained" startIcon={<AddCircleIcon />} onClick={() => setFormVisible(true)} sx={{ marginBottom: 2 }}>
            Nuevo Rol
          </Button>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roles.map((role) => (
                  <TableRow key={role.id}>
                    <TableCell>{role.nombre}</TableCell>
                    <TableCell>
                      <IconButton color="primary" onClick={() => handleEdit(role)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton color="secondary" onClick={() => handleOpenDeleteDialog(role.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: "bold", marginBottom: 2 }}>
              {editMode ? "Editar Rol" : "Nuevo Rol"}
            </Typography>
            <IconButton onClick={() => setFormVisible(false)} color="primary">
              <CloseIcon />
            </IconButton>
          </Box>
          <form onSubmit={handleSubmit}>
            <TextField label="Nombre" name="nombre" value={formData.nombre} onChange={handleChange} fullWidth required margin="normal" />
            <Button type="submit" variant="contained" fullWidth>
              {editMode ? "Actualizar Rol" : "Guardar Rol"}
            </Button>
          </form>
        </Box>
      )}
      <Snackbar open={alertOpen} autoHideDuration={6000} onClose={() => setAlertOpen(false)}>
        <Alert onClose={() => setAlertOpen(false)} severity={alertSeverity}>{alertMessage}</Alert>
      </Snackbar>

      {/* Modal de Confirmación de Eliminación */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirmación</DialogTitle>
        <DialogContent>
          <Typography>¿Estás seguro de eliminar este contacto?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancelar
          </Button>
          <Button onClick={() => handleDelete(currentRoleId)} color="secondary">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RegistroRoles;
